import type { ChatCopyPack } from '../../config/types';
import { chatCopy } from '../../config';

/**
 * Formats a Unix-ms timestamp as a coarse relative-time string.
 * Returns "" if timestamp is falsy. Resolution: days > hours > minutes > "just now".
 *
 * Shared by ConversationHistoryList and HamburgerDrawer so both surfaces
 * agree on the same wording.
 *
 * #2109 R3-B24: the core picks the unit and the count; the copy pack words
 * it (`time.ago`, Intl.RelativeTimeFormat in the reader's locale), so no
 * English suffix is assembled here. English stays "3d ago" / "just now".
 */
export function formatRelativeTime(timestamp: number, time: ChatCopyPack['time'] = chatCopy().time): string {
  if (!timestamp) return '';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return time.ago(days, 'day');
  if (hours > 0) return time.ago(hours, 'hour');
  if (minutes > 0) return time.ago(minutes, 'minute');
  return time.justNow;
}

// ─── Export slice presets (#270) ─────────────────────────────────────
//
// Time-based slice selection for conversation export: "Everything" /
// "Today" / "This week". Boundaries are computed in the user’s LOCAL
// timezone via the local-component Date constructor, which is DST-correct:
// `new Date(y, m, d)` always lands on local midnight of that calendar day,
// whether the day is 23, 24, or 25 hours long. Never derive boundaries by
// subtracting fixed millisecond spans — that drifts across DST changes.

/**
 * Slice preset for conversation export (#270).
 * #269 adds 'custom': a user-marked start/end message range. Custom has no
 * time-derived window — callers slice by message INDEX via
 * message-selection.ts and, for the server-side .docx export, pass the
 * anchor messages' own timestamps. The time helpers below therefore treat
 * 'custom' like 'everything' (no time filtering) — do NOT rely on them for
 * custom slicing.
 */
export type SlicePreset = 'everything' | 'today' | 'week' | 'custom';

/** Local midnight at the start of `now`'s calendar day. */
export function startOfLocalDay(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Local midnight at the start of `now`'s week (weeks start on Monday).
 * Day arithmetic happens in local calendar components, so a DST change
 * mid-week does not shift the boundary.
 */
export function startOfLocalWeek(now: Date = new Date()): Date {
  const daysSinceMonday = (now.getDay() + 6) % 7; // Sun=6, Mon=0, …, Sat=5
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday);
}

/**
 * Resolve a preset to an inclusive [startTime, endTime] range in local time,
 * or null when the preset carries no time window: 'everything' (no
 * filtering) and 'custom' (#269 — sliced by message index, not by time).
 */
export function getPresetRange(
  preset: SlicePreset,
  now: Date = new Date(),
): { startTime: Date; endTime: Date } | null {
  if (preset === 'everything' || preset === 'custom') return null;
  const startTime = preset === 'today' ? startOfLocalDay(now) : startOfLocalWeek(now);
  return { startTime, endTime: now };
}

/**
 * Filter items carrying a timestamp down to the preset’s local-time window.
 * Accepts Date | string | number timestamps; invalid timestamps are excluded
 * (except under 'everything', which never filters).
 */
export function filterByPreset<T extends { timestamp: Date | string | number }>(
  items: T[],
  preset: SlicePreset,
  now: Date = new Date(),
): T[] {
  const range = getPresetRange(preset, now);
  if (!range) return items;
  const startMs = range.startTime.getTime();
  const endMs = range.endTime.getTime();
  return items.filter((item) => {
    const t = new Date(item.timestamp).getTime();
    return !Number.isNaN(t) && t >= startMs && t <= endMs;
  });
}

/** Count of epoch-ms timestamps that fall inside the preset’s window. */
export function countTimestampsInPreset(
  timestamps: number[],
  preset: SlicePreset,
  now: Date = new Date(),
): number {
  const range = getPresetRange(preset, now);
  if (!range) return timestamps.length;
  const startMs = range.startTime.getTime();
  const endMs = range.endTime.getTime();
  return timestamps.filter((t) => !Number.isNaN(t) && t >= startMs && t <= endMs).length;
}
